/**
 * EIP-712 typed data for Nado's `withdraw_collateral_v2` execute.
 *
 * @see {@link https://docs.nado.xyz/developer-resources/api/gateway/executes/withdraw-collateral | Nado withdraw collateral execute}
 */

import type { Address, Hex } from 'viem'
import { INK_CHAIN_ID, NADO_EIP712_VERIFYING_CONTRACT_ADDRESS } from './consts'

export const WITHDRAW_COLLATERAL_V2_TYPES = {
  WithdrawCollateralV2: [
    { name: 'sender', type: 'bytes32' },
    { name: 'productId', type: 'uint32' },
    { name: 'amount', type: 'uint128' },
    { name: 'nonce', type: 'uint64' },
    { name: 'sendTo', type: 'address' },
    // Reserved by Nado — must be signed as 0
    { name: 'appendix', type: 'uint128' },
  ],
} as const

export interface WithdrawCollateralV2Params {
  /** Packed subaccount id (see `buildNadoSubaccountId`). */
  sender: Hex
  productId: number
  /** Raw base units in the token's native decimals — not x18. */
  amount: bigint
  /** `tx_nonce` from the nonces query, fetched immediately before signing. */
  nonce: bigint
  /** Payout recipient. Non-zero forces owner-wallet signing on Nado's side. */
  sendTo: Address
}

/**
 * Typed data for Nado's `withdraw_collateral_v2` execute. `appendix` is
 * reserved by Nado and must be 0.
 */
export function buildWithdrawCollateralV2TypedData(
  params: WithdrawCollateralV2Params,
) {
  return {
    domain: {
      name: 'Nado',
      version: '0.0.1',
      chainId: INK_CHAIN_ID,
      verifyingContract: NADO_EIP712_VERIFYING_CONTRACT_ADDRESS,
    },
    types: WITHDRAW_COLLATERAL_V2_TYPES,
    primaryType: 'WithdrawCollateralV2',
    message: {
      sender: params.sender,
      productId: params.productId,
      amount: params.amount,
      nonce: params.nonce,
      sendTo: params.sendTo,
      appendix: 0n, // set to 0, Nado reserved
    },
  } as const
}
